import type { AxiosCacheInstance } from 'axios-cache-interceptor';

import type {
  Lithology,
  StratColumn,
  StratUnit,
  Formation,
  Sequence,
  FormationOption,
  StratUnitOption,
  SectionOption,
  DrillingAnalysisGroup,
  GoStorageObject,
  IDashboardService,
  SelectRequestConfig,
  VideoBroadcast,
  Wellbore,
} from './IDashboardService';

export class DashboardService implements IDashboardService {
  baseURL: string = '/service/v1';

  constructor(private axios: AxiosCacheInstance) {}

  getVideoBroadcast(wellId: number, abortController?: AbortController) {
    return this.axios
      .get<VideoBroadcast[]>(`${this.baseURL}/wells/${wellId}/videoBroadcasts`, {
        signal: abortController?.signal,
        cache: {
          ttl: 1000 * 10,
        },
      })
      .then(({ data }) => data);
  }

  getWellbores(wellId: number, abortController?: AbortController) {
    return this.axios
      .get<Wellbore[]>(`${this.baseURL}/wells/${wellId}/wellbores`, {
        signal: abortController?.signal,
        cache: {
          ttl: 1000 * 5,
        },
      })
      .then(({ data }) => data);
  }

  selectData<T>(config: SelectRequestConfig, abortController?: AbortController): Promise<GoStorageObject<T>[]> {
    return this.axios
      .post<Record<string, GoStorageObject<T>>[]>(`${this.baseURL}/object/select?flatResponse=false`, config, {
        signal: abortController?.signal,
      })
      .then(({ data }) =>
        data.map((item) => {
          const key = config.joinedAlias;
          return item[key];
        }),
      );
  }

  createGoStorageObject<T>(objectType: string, data: T): Promise<GoStorageObject<T>> {
    return this.axios
      .post<GoStorageObject<T>>(`${this.baseURL}/object/new/${objectType}`, data)
      .then(({ data }) => data);
  }

  updateGoStorageObject<T>(objectType: string, id: number, data: T): Promise<GoStorageObject<T>> {
    return this.axios
      .post<GoStorageObject<T>>(`${this.baseURL}/object/${objectType}/${id}`, data)
      .then(({ data }) => data);
  }

  deleteGoStorageObject(objectType: string, id: number): Promise<void> {
    return this.axios.delete(`${this.baseURL}/object/${objectType}/${id}`);
  }
  getLithologyByWellboreId(wellboreId: number, isPlan = false, abortController?: AbortController) {
    return this.axios.get<Lithology[]>(`${this.baseURL}/lithology/by-wellbore/${wellboreId}`, {
      signal: abortController?.signal,
      cache: {
        ttl: 1000 * 5,
      },
      params: {
        isPlan,
      },
    });
  }

  getStratColumnsByWellboreId(wellboreId: number, isPlan = false, abortController?: AbortController) {
    return this.axios.get<StratColumn[]>(`${this.baseURL}/strat-columns/by-wellbore/${wellboreId}`, {
      signal: abortController?.signal,
      cache: {
        ttl: 1000 * 5,
      },
      params: {
        isPlan,
      },
    });
  }

  getStratUnitsByStratColumnId(stratColumnId: number, abortController?: AbortController) {
    return this.axios.get<StratUnit[]>(`${this.baseURL}/strat-units/by-strat-column/${stratColumnId}`, {
      signal: abortController?.signal,
      cache: {
        ttl: 1000 * 5,
      },
    });
  }

  getFormationsByLithologyId(lithologyId: number, abortController?: AbortController) {
    return this.axios.get<Formation[]>(`${this.baseURL}/formations/by-lithology/${lithologyId}`, {
      signal: abortController?.signal,
      cache: {
        ttl: 1000 * 5,
      },
    });
  }

  getSequenceByWellId(wellId: number, abortController?: AbortController) {
    return this.axios.get<Sequence[]>(`${this.baseURL}/wells/${wellId}/details/fact/sequence`, {
      signal: abortController?.signal,
    });
  }

  getFormationsOptions(abortController?: AbortController) {
    return this.axios
      .get<Array<GoStorageObject<FormationOption>>>(`${this.baseURL}/object/list/Common_Reservoir`, {
        signal: abortController?.signal,
      })
      .then(({ data }) => data)
      .then((data) =>
        data.map((t) => ({
          ...t.data,
          id: t.id,
        })),
      );
  }

  getStratUnitsOptions(abortController?: AbortController) {
    return this.axios
      .get<Array<GoStorageObject<StratUnitOption>>>(`${this.baseURL}/object/list/Common_StratUnitType`, {
        signal: abortController?.signal,
      })
      .then(({ data }) => data)
      .then((data) =>
        data.map((t) => ({
          ...t.data,
          id: t.id,
        })),
      );
  }

  getSectionsOptions(abortController?: AbortController) {
    return this.axios
      .get<Array<GoStorageObject<SectionOption>>>(`${this.baseURL}/object/list/Common_SectionType`, {
        signal: abortController?.signal,
      })
      .then(({ data }) => data)
      .then((data) =>
        data.map((t) => ({
          ...t.data,
          id: t.id,
        })),
      );
  }

  getCorridorsByWellboreId(wellboreId: number, abortController?: AbortController) {
    return this.axios.get<Array<DrillingAnalysisGroup>>(`${this.baseURL}/corridors/by-wellbore/${wellboreId}`, {
      signal: abortController?.signal,
    });
  }
}
